// Condiciones de lanzamiento. Ver docs/theory/environment.md.

export interface LaunchConditions {
  /** Altitud del sitio sobre el nivel del mar (m). */
  readonly siteAltitude: number;
  /** Latitud del sitio (rad). */
  readonly latitude: number;
  /** Longitud del sitio (rad). */
  readonly longitude: number;
  /** Longitud útil de la varilla (m). */
  readonly rodLength: number;
  /** Ángulo de la varilla respecto a la vertical (rad). */
  readonly rodAngle: number;
  /** Acimut de la varilla, horario desde el norte (rad). */
  readonly rodDirection: number;
  /** Temperatura medida en el sitio (K). */
  readonly temperature: number;
  /** Presión medida en el sitio (Pa). */
  readonly pressure: number;
}

export const DEFAULT_LAUNCH: LaunchConditions = {
  siteAltitude: 0,
  latitude: (28.61 * Math.PI) / 180,
  longitude: 0,
  rodLength: 1,
  rodAngle: 0,
  rodDirection: 0,
  temperature: 288.15,
  pressure: 101325,
};

export type LaunchErrors = Partial<Record<keyof LaunchConditions, string>>;

type Range = readonly [min: number, max: number, minInclusive: boolean, maxInclusive: boolean, label: string];

const RANGES: Record<keyof LaunchConditions, Range> = {
  siteAltitude: [-500, 9000, true, true, 'entre -500 y 9000 m'],
  latitude: [-Math.PI / 2, Math.PI / 2, true, true, 'entre -90° y 90°'],
  longitude: [-Math.PI, Math.PI, true, true, 'entre -180° y 180°'],
  rodLength: [0, 30, false, true, 'mayor que 0 y como máximo 30 m'],
  rodAngle: [0, Math.PI / 2, true, false, 'desde 0° hasta menos de 90°'],
  rodDirection: [-Infinity, Infinity, true, true, 'un número finito'],
  temperature: [180, 340, true, true, 'entre 180 y 340 K'],
  pressure: [20_000, 120_000, true, true, 'entre 20000 y 120000 Pa'],
};

export function validateLaunch(c: LaunchConditions): LaunchErrors {
  const errors: LaunchErrors = {};
  for (const key of Object.keys(RANGES) as Array<keyof LaunchConditions>) {
    const [min, max, minIn, maxIn, label] = RANGES[key];
    const v = c[key];
    const ok = Number.isFinite(v) && (minIn ? v >= min : v > min) && (maxIn ? v <= max : v < max);
    if (!ok) errors[key] = `Valor fuera de rango: debe estar ${label}`;
  }
  return errors;
}

export interface LaunchUpdate {
  readonly conditions: LaunchConditions;
  readonly errors: LaunchErrors;
}

/** Aplica un cambio; los campos inválidos se rechazan y se conserva el valor válido previo. */
export function updateLaunch(prev: LaunchConditions, patch: Partial<LaunchConditions>): LaunchUpdate {
  const candidate = { ...prev, ...patch };
  const errors = validateLaunch(candidate);
  const next: Record<string, number> = { ...candidate };
  for (const key of Object.keys(errors)) next[key] = prev[key as keyof LaunchConditions];
  return { conditions: next as unknown as LaunchConditions, errors };
}
